// Oda teklifi paylaşımı: tarih/tutar biçimleri, PDF linki ve mesaj şablonları — saf fonksiyonlar.
//
// Tarihler: API 'DATE' kolonlarını (checkIn/checkOut) ISO zaman damgası döndürür ve sunucu saat dilimi yüzünden
// bir gün geri kayar ("2026-12-25" → "2026-12-24T21:00:00.000Z"). dateKey her biçimi İstanbul gününe çevirir;
// ham değer ASLA müşteri mesajına basılmaz.
//
// Şablonlar Ayarlar > Mesaj Şablonları'ndan (settings kategorisi 'messages') değiştirilebilir; alan boşsa
// DEFAULT_MESSAGES kullanılır. Yer tutucular: {customerName} {rooms} {checkIn} {checkOut} {stay} {nights}
// {totalPrice} {link} {offerNo} {priceListLink} {hotelPhone} {signature}. Bilinmeyen yer tutucu olduğu gibi kalır.
import { formatMoneyTR } from './pricing';

const TZ = 'Europe/Istanbul';
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const DAY_MS = 86400000;

let keyFormatter = null;
const istanbulKey = (date) => {
  if (!keyFormatter) {
    keyFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
  }
  const parts = keyFormatter.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

// Her tarih biçimi → 'YYYY-MM-DD' (İstanbul günü). Geçersiz/boş → ''.
export const dateKey = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    // Saat dilimi bilgisi olmayan tarih-saat ("2026-10-07 23:30:00") İstanbul yerel saati kabul edilir
    // (tarayıcının saat dilimine göre okunsaydı başka ülkede bir gün kayardı)
    const local = /^(\d{4}-\d{2}-\d{2})[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.exec(value);
    if (local) return local[1];
  }
  const d = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(d.getTime()) ? '' : istanbulKey(d);
};

// Bugünün İstanbul günü
export const todayKey = (now = new Date()) => istanbulKey(now);

const keyToUtc = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};
// İki tarih arasındaki gün farkı (b − a). Biri geçersizse null.
export const daysBetween = (a, b) => {
  const ka = dateKey(a);
  const kb = dateKey(b);
  if (!ka || !kb) return null;
  return Math.round((keyToUtc(kb) - keyToUtc(ka)) / DAY_MS);
};

// "25 Aralık 2026"
export const formatDateTR = (value) => {
  const k = dateKey(value);
  if (!k) return '';
  const [y, m, d] = k.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

// "25-28 Aralık 2026" · "30 Aralık 2026 - 2 Ocak 2027" · "28 Şubat - 3 Mart 2027"
export const formatDateRangeTR = (from, to) => {
  const a = dateKey(from);
  const b = dateKey(to);
  if (!a && !b) return '';
  if (!a || !b) return formatDateTR(a || b);
  const [y1, m1, d1] = a.split('-').map(Number);
  const [y2, m2, d2] = b.split('-').map(Number);
  if (y1 !== y2) return `${formatDateTR(a)} - ${formatDateTR(b)}`;
  if (m1 !== m2) return `${d1} ${MONTHS[m1 - 1]} - ${d2} ${MONTHS[m2 - 1]} ${y2}`;
  return `${d1}-${d2} ${MONTHS[m2 - 1]} ${y2}`;
};

// Gece sayısı: kayıttaki nights, yoksa tarihlerden
export const offerNights = (offer) => {
  const n = Number(offer?.nights);
  if (Number.isFinite(n) && n > 0) return n;
  const diff = daysBetween(offer?.checkIn, offer?.checkOut);
  return diff && diff > 0 ? diff : 0;
};

// "25-28 Aralık 2026 (3 gece)"
export const formatStay = (offer) => {
  const range = formatDateRangeTR(offer?.checkIn, offer?.checkOut);
  const nights = offerNights(offer);
  if (!range) return nights ? `${nights} gece` : '';
  return nights ? `${range} (${nights} gece)` : range;
};

// "85.000 TL" (totalPrice = indirim sonrası ödenecek tutar)
export const offerMoneyText = (offer) => `${formatMoneyTR(Number(offer?.totalPrice) || 0)} ${offer?.currency || 'TL'}`;

// "Deluxe Oda ×2, Suit" — aynı oda tipi tekrar ediyorsa adetle
export const offerRoomsText = (offer) => {
  const list = Array.isArray(offer?.rooms) && offer.rooms.length > 0 ? offer.rooms : (offer?.room ? [offer.room] : []);
  const counts = new Map();
  list.forEach((r) => {
    const name = String(r?.name || '').trim();
    if (name) counts.set(name, (counts.get(name) || 0) + 1);
  });
  return [...counts.entries()].map(([name, n]) => (n > 1 ? `${name} ×${n}` : name)).join(', ');
};

// PDF dosya adı — OfferForm bu adla yükler, OfferPDF'teki QR kod da bu adı gösterir. ALGORİTMA DEĞİŞMEMELİ: müşteriye
// gitmiş eski linkler buna bağlı (toLowerCase bilerek Türkçe değil: 'İ' → 'i̇' → "i-..." eski dosya adlarıyla aynı kalsın).
// Tek ek: ad 200 karakterle sınırlı (daha uzun adla dosya zaten hiç yazılamıyordu — dosya adı sınırı 255 bayt).
const PDF_SLUG_MAX = 200;
export const offerPdfFileName = (offer) => {
  if (!offer || !offer.customerName) return `${offer?.id}.pdf`;
  const slug = offer.customerName
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, PDF_SLUG_MAX)
    .replace(/-$/, '');
  return `${slug}-${offer.id}.pdf`;
};

// Müşteriye giden link: HER ZAMAN PDF dosyası (sayısal /offers/view/<id> linki artık kullanılmaz)
export const offerLink = (offer, origin = (typeof window !== 'undefined' ? window.location.origin : '')) =>
  `${origin}/offers/view/${offerPdfFileName(offer)}`;

// Telefon → wa.me biçimi (TR varsayımı; '00' ile başlayan uluslararası numara da desteklenir). Boş → ''.
export const waPhone = (raw) => {
  let phone = String(raw || '').replace(/\D/g, '');
  if (!phone) return '';
  if (phone.startsWith('00')) phone = phone.slice(2);
  else if (phone.startsWith('0')) phone = `9${phone}`;
  else if (phone.length === 10) phone = `90${phone}`;
  return phone;
};

export const DEFAULT_PRICE_LIST_LINK = 'https://ramadaergan.com/subat-ayi-fiyat-listesi';

export const DEFAULT_MESSAGES = {
  signature: [
    'Detaylı bilgi ve onay için iletişime geçebilirsiniz.',
    '444 1 344',
    'info@ramadaergan.com',
    'www.ramadaergan.com',
  ].join('\n'),
  waDetailed: [
    'Sayın {customerName},',
    'Ramada by Wyndham Ergan rezervasyon teklifiniz:',
    '',
    'Oda: {rooms}',
    'Tarih: {stay}',
    'Tutar: {totalPrice}',
    '',
    'Teklifinizi görüntülemek için:',
    '{link}',
    '',
    '{signature}',
  ].join('\n'),
  waLink: [
    'Sayın {customerName}, Ramada by Wyndham Ergan ön rezervasyon formunuzu ({nights} gece) aşağıdaki linkten görüntüleyebilirsiniz:',
    '',
    '{link}',
    '',
    '{signature}',
  ].join('\n'),
  instagram: [
    'Sayın {customerName},',
    'Ramada by Wyndham Ergan rezervasyon teklifiniz:',
    '',
    'Oda: {rooms}',
    'Tarih: {stay}',
    'Tutar: {totalPrice}',
    '',
    'Teklifinizi görüntülemek için:',
    '{link}',
    '',
    '{signature}',
  ].join('\n'),
  priceList: [
    'Sayın {customerName}, otelimizin oda fiyat listesini aşağıdaki linkte inceleyebilirsiniz.',
    '',
    'Fiyat Listesi: {priceListLink}',
    '',
    '{signature}',
  ].join('\n'),
  priceListLink: '',
};

export const DEFAULT_EMAIL_SUBJECT = 'Ramada by Wyndham Ergan - Rezervasyon Teklifi';
export const DEFAULT_EMAIL_BODY = [
  'Sayın {customerName},',
  '',
  'Ramada by Wyndham Ergan ön rezervasyon formunuzu aşağıdaki linkten görüntüleyebilirsiniz:',
  '',
  '{link}',
  '',
  'Saygılarımızla,',
  'Ramada by Wyndham Ergan',
].join('\n');

// Şablonlarda kullanılabilecek yer tutucular (Ayarlar ekranındaki yardım listesi)
export const TEMPLATE_PLACEHOLDERS = [
  ['{customerName}', 'Müşteri adı'],
  ['{rooms}', 'Oda tipleri (ör. Deluxe Oda ×2, Suit)'],
  ['{stay}', 'Tarih ve gece (ör. 25-28 Aralık 2026 (3 gece))'],
  ['{checkIn}', 'Giriş tarihi (ör. 25 Aralık 2026)'],
  ['{checkOut}', 'Çıkış tarihi'],
  ['{nights}', 'Gece sayısı'],
  ['{totalPrice}', 'Ödenecek tutar (ör. 85.000 TL)'],
  ['{link}', 'Teklif PDF linki'],
  ['{offerNo}', 'Teklif numarası'],
  ['{priceListLink}', 'Fiyat listesi linki'],
  ['{hotelPhone}', 'Otel telefonu (Ayarlar > Marka)'],
  ['{signature}', 'İmza / iletişim satırları'],
];

// Ayar değeri boşsa varsayılan
export const messageTemplate = (messages, key) => {
  const v = messages?.[key];
  return typeof v === 'string' && v.trim() ? v : DEFAULT_MESSAGES[key];
};

// {ad} yer tutucularını doldurur; bilinmeyen yer tutucu olduğu gibi bırakılır (önizlemede görünsün)
export const fillTemplate = (template, vars) => String(template ?? '').replace(/\{(\w+)\}/g, (all, key) => (
  Object.prototype.hasOwnProperty.call(vars, key) && vars[key] !== undefined && vars[key] !== null ? String(vars[key]) : all
));

// Bir teklif için tüm yer tutucu değerleri. ctx: { messages, logos, origin }
export const offerTemplateVars = (offer, ctx = {}) => {
  const messages = ctx.messages || {};
  const logos = ctx.logos || {};
  const priceListLink = (typeof messages.priceListLink === 'string' && messages.priceListLink.trim())
    || (typeof logos.roomsLink === 'string' && logos.roomsLink.trim())
    || DEFAULT_PRICE_LIST_LINK;
  const vars = {
    customerName: String(offer?.customerName || '').trim(),
    rooms: offerRoomsText(offer),
    stay: formatStay(offer),
    checkIn: formatDateTR(offer?.checkIn),
    checkOut: formatDateTR(offer?.checkOut),
    nights: offerNights(offer),
    totalPrice: offerMoneyText(offer),
    link: offer?.id ? offerLink(offer, ctx.origin) : '',
    offerNo: offer?.id ? String(offer.id) : '',
    priceListLink,
    hotelPhone: String(logos.hotelPhone || '444 1 344'),
  };
  // İmza da tüm yer tutucularla dolar ({signature} hariç — kendini içeremez)
  vars.signature = fillTemplate(messageTemplate(messages, 'signature'), vars);
  return vars;
};

// kind: 'waDetailed' | 'waLink' | 'instagram' | 'priceList'
export const buildOfferMessage = (kind, offer, ctx = {}) =>
  fillTemplate(messageTemplate(ctx.messages, kind), offerTemplateVars(offer, ctx)).replace(/\n{3,}/g, '\n\n').trim();

// E-posta (konu/gövde Ayarlar > E-posta Şablonu'ndan: settings 'bank'.emailSubject / emailBody)
export const buildOfferEmail = (offer, ctx = {}) => {
  const bank = ctx.bank || {};
  const vars = offerTemplateVars(offer, ctx);
  const subject = typeof bank.emailSubject === 'string' && bank.emailSubject.trim() ? bank.emailSubject : DEFAULT_EMAIL_SUBJECT;
  const body = typeof bank.emailBody === 'string' && bank.emailBody.trim() ? bank.emailBody : DEFAULT_EMAIL_BODY;
  return { subject: fillTemplate(subject, vars), body: fillTemplate(body, vars) };
};

// Paylaşım adresleri
export const whatsappUrl = (phone, text) => `https://wa.me/${waPhone(phone)}?text=${encodeURIComponent(text)}`;
export const mailtoUrl = (email, subject, body) =>
  `mailto:${String(email || '').trim()}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
export const instagramUrl = (username) => `https://ig.me/m/${String(username || '').trim().replace(/^@+/, '')}`;
